import { Metadata } from "next";
// Removed import of `cookies` from "next/headers" to avoid dynamic server usage which caused static-to-dynamic runtime errors

import { SupportedLocale } from "@/enums/locale.enum";
import { routing } from "@/i18n/routing";
import { seoService } from "@/services/seo.service";
import { ISeoMetadata } from "@/types/seo";
import {
  getDefaultLocale,
  getSupportedLocales,
  isDefaultLocale,
} from "@/utils/locale";
import { createLogger } from "@/utils/logger";
import { buildQueryStringFromFilters } from "@/utils/queryString";

import SeoHeadClient from "./SeoHead.client";
import { SeoHeadProps } from "./SeoHead.types";

const logger = createLogger("seo-head");

// Встроенная функция для получения базового URL
const getBaseUrl = () => {
  if (typeof window !== "undefined") {
    return "";
  } // We're in the browser, no need for absolute URLs
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }
  return `http://localhost:${process.env.PORT || 3000}`;
};

const DEFAULT_TITLE = "Avezor - Real Estate Portal";
const DEFAULT_DESCRIPTION = "Find your dream property with Avezor";
const DEFAULT_KEYWORDS = "real estate, property, apartments, houses, Avezor";
const DEFAULT_OG_IMAGE = "/images/og-image.png";

export default async function SeoHead({
  url,
  pageType,
  webPageProps,
  articleProps,
}: SeoHeadProps) {
  if (!url) {
    return null;
  }
  const metadata = await fetchSeoMetadata(url);

  // Log props directly in the server component body
  logger.info("SeoHead rendering with data:", {
    fetchedTitle: metadata?.title,
    fetchedDescription: metadata?.description,
    fetchedKeywords: metadata?.keywords,
    fetchedCanonical: metadata?.canonical,
    fetchedOgImage: metadata?.ogImage,
    fetchedOgTitle: metadata?.og?.["og:title"],
    fetchedOgDescription: metadata?.og?.["og:description"],
    passedUrl: url,
    passedPageType: pageType,
    passedWebPageProps: webPageProps,
    passedArticleProps: articleProps,
  });

  return (
    <SeoHeadClient
      url={url}
      pageType={pageType}
      webPageProps={webPageProps}
      articleProps={articleProps}
      metadata={metadata}
    />
  );
}

function applyGeneratedMetadata(
  metadata: ISeoMetadata,
  currentAbsoluteUrl: string,
): Metadata {
  const baseUrl = process.env.NEXT_PUBLIC_FRONT_URL || "https://avezor.com";
  const siteDefaultLocale = routing.defaultLocale;

  const finalCanonicalUrl = determineCanonicalUrl(
    metadata,
    currentAbsoluteUrl,
    baseUrl,
    siteDefaultLocale,
  );

  const ogImageUrl = getOpenGraphImageUrl(metadata, baseUrl);

  const robotsDirectives = processRobotsDirectives(metadata);

  const nextMetadata: Metadata = {
    title: metadata.title || DEFAULT_TITLE,
    description: metadata.description || DEFAULT_DESCRIPTION,
    keywords: metadata.keywords || DEFAULT_KEYWORDS,
    openGraph: {
      title: metadata.og?.["og:title"] || metadata.title || DEFAULT_TITLE,
      description:
        metadata.og?.["og:description"] ||
        metadata.description ||
        DEFAULT_DESCRIPTION,
      type: (metadata.og?.["og:type"] || "website") as any,
      url: finalCanonicalUrl,
      images: ogImageUrl
        ? [
            {
              url: ogImageUrl,
              width: 1200,
              height: 630,
              alt: metadata.title || DEFAULT_TITLE,
            },
          ]
        : undefined,
      locale: metadata.locale,
      siteName: "Avezor",
    },
    twitter: {
      card: "summary_large_image",
      title: metadata.og?.["og:title"] || metadata.title || DEFAULT_TITLE,
      description:
        metadata.og?.["og:description"] ||
        metadata.description ||
        DEFAULT_DESCRIPTION,
      images: ogImageUrl ? [ogImageUrl] : undefined,
      creator: "@avezor",
      site: "@avezor",
    },
    robots: {
      index: !robotsDirectives.noindex,
      follow: !robotsDirectives.nofollow,
      noarchive: robotsDirectives.noarchive,
      googleBot: {
        index: !robotsDirectives.noindex,
        follow: !robotsDirectives.nofollow,
        noimageindex: robotsDirectives.noimageindex,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
    alternates: {
      canonical: finalCanonicalUrl,
      languages: generateHreflangTags(metadata, baseUrl, siteDefaultLocale),
    },
  };
  return nextMetadata;
}

function determineCanonicalUrl(
  metadata: ISeoMetadata,
  currentAbsoluteUrl: string,
  baseUrl: string,
  siteDefaultLocale: string,
): string {
  if (metadata.canonical) {
    if (metadata.canonical.startsWith("http")) {
      return metadata.canonical;
    } else {
      return `${baseUrl}${metadata.canonical}`;
    }
  }

  if (metadata.locale === siteDefaultLocale) {
    const prefix = `/${siteDefaultLocale}`;
    if (currentAbsoluteUrl.startsWith(`${baseUrl}${prefix}/`)) {
      return `${baseUrl}${currentAbsoluteUrl.substring(`${baseUrl}${prefix}`.length)}`;
    } else if (currentAbsoluteUrl.startsWith(`${baseUrl}${prefix}`)) {
      return `${baseUrl}${currentAbsoluteUrl.substring(`${baseUrl}${prefix}`.length) || "/"}`;
    }
  }

  if (currentAbsoluteUrl === baseUrl) {
    return `${baseUrl}/`;
  }

  return currentAbsoluteUrl;
}

function getOpenGraphImageUrl(metadata: ISeoMetadata, baseUrl: string): string {
  const effectiveOgImage =
    metadata.ogImage ||
    metadata.og?.["og:image"] ||
    metadata.og?.["image"] ||
    DEFAULT_OG_IMAGE;

  return effectiveOgImage.startsWith("http")
    ? effectiveOgImage
    : `${baseUrl}${effectiveOgImage.startsWith("/") ? effectiveOgImage : `/${effectiveOgImage}`}`;
}

function processRobotsDirectives(metadata: ISeoMetadata): {
  noindex: boolean;
  nofollow: boolean;
  noarchive: boolean;
  noimageindex: boolean;
} {
  const robotsDirective = metadata.robots?.toLowerCase() || "";

  return {
    noindex: metadata.noindex === true || robotsDirective.includes("noindex"),
    nofollow: robotsDirective.includes("nofollow"),
    noarchive: robotsDirective.includes("noarchive"),
    noimageindex: robotsDirective.includes("noimageindex"),
  };
}

/**
 * Generates hreflang tags for alternate language versions
 */
function generateHreflangTags(
  metadata: ISeoMetadata,
  baseUrl: string,
  siteDefaultLocale: string,
): Record<string, string> {
  const result: Record<string, string> = {};

  // Get canonical URL for reference
  const pageCanonicalUrl = metadata.canonical
    ? metadata.canonical.startsWith("http")
      ? metadata.canonical
      : `${baseUrl}${metadata.canonical}`
    : metadata.url
      ? metadata.url.startsWith("http")
        ? metadata.url
        : `${baseUrl}${metadata.url}`
      : `${baseUrl}/`;

  // Handle data from metadata.hreflangs if available
  if (metadata.hreflangs && metadata.hreflangs.length > 0) {
    // Process provided hreflang data
    metadata.hreflangs.forEach((hreflang: { lang: string; url: string }) => {
      const langCode =
        hreflang.lang === "default" ? "x-default" : hreflang.lang;
      const url = hreflang.url.startsWith("http")
        ? hreflang.url
        : `${baseUrl}${hreflang.url}`;
      result[langCode] = url;
    });
  } else if (metadata.url) {
    // If no explicit hreflangs but we have URL data, infer based on current URL
    // Add current page language
    const currentLang = metadata.locale || siteDefaultLocale;
    result[currentLang] = pageCanonicalUrl;

    // For default locale, add x-default tag
    if (currentLang === siteDefaultLocale) {
      result["x-default"] = pageCanonicalUrl;
    }
  }

  return result;
}

/**
 * Generates default JSON-LD structured data for the page
 */
function generateDefaultJsonLd(metadata: ISeoMetadata, baseUrl: string): any {
  // Basic WebPage schema
  const basicSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: metadata.title || DEFAULT_TITLE,
    description: metadata.description || DEFAULT_DESCRIPTION,
    url: metadata.canonical?.startsWith("http")
      ? metadata.canonical
      : metadata.canonical
        ? `${baseUrl}${metadata.canonical}`
        : `${baseUrl}${metadata.url ? metadata.url : "/"}`,
  };

  // For property listings, enhance with more detailed information
  if (
    metadata.url?.includes("/property/") ||
    metadata.url?.includes("/listing/")
  ) {
    return {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: metadata.title || DEFAULT_TITLE,
      description: metadata.description || DEFAULT_DESCRIPTION,
      url: metadata.canonical?.startsWith("http")
        ? metadata.canonical
        : metadata.canonical
          ? `${baseUrl}${metadata.canonical}`
          : `${baseUrl}${metadata.url ? metadata.url : "/"}`,
      image: getOpenGraphImageUrl(metadata, baseUrl),
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        priceCurrency: metadata.og?.["og:price:currency"] || "EUR",
        price: metadata.og?.["og:price:amount"] || "0",
      },
      address: {
        "@type": "PostalAddress",
        addressLocality: metadata.og?.["og:locality"] || "",
        addressRegion: metadata.og?.["og:region"] || "",
        addressCountry: metadata.og?.["og:country"] || "Greece",
      },
    };
  }

  return basicSchema;
}

async function fetchSeoMetadata(url: string): Promise<ISeoMetadata | null> {
  try {
    const fullUrl = `${getBaseUrl()}/${locale}${url}`;
    // logger.info(`Fetching SEO metadata for URL: ${fullUrl}`); // Log before fetching
    const data = await seoService.getMetadata({
      url: fullUrl,
      locale: locale as SupportedLocale,
      queryParams: {},
    });
    // logger.info("Fetched SEO metadata:", data); // Log after fetching
    return data;
  } catch (error) {
    logger.error(`Error fetching SEO metadata for ${url}:`, error);
    return null;
  }
}

// export const revalidate = 600;
export const dynamic = "force-dynamic";

export async function generateMetadata({
  params,
  searchParams,
}: {
  params: { locale?: string; slug?: string | string[] };
  searchParams?: { [key: string]: string | string[] | undefined };
}): Promise<Metadata> {
  const currentLocale = params?.locale || (await getDefaultLocale());
  let pagePath = "";

  if (params?.slug) {
    if (Array.isArray(params.slug)) {
      pagePath = "/" + params.slug.join("/");
    } else {
      pagePath = "/" + params.slug;
    }
  }
  // Handle root paths for locales or default
  if (!pagePath) {
    pagePath = "/"; // Default to root if no slug
  }
  // Ensure pagePath doesn't become just "/" if it was already intended for locale root like /en
  // The logic for fullPath in fetchSeoMetadata seems more robust, let's adapt
  const requestUrl = params?.slug
    ? Array.isArray(params.slug)
      ? params.slug.join("/")
      : params.slug
    : ""; // Empty string for homepage at the root of the locale

  // Construct the relative path for the API call (without locale prefix if default)
  // And without query string for the `url` parameter of seoService.getMetadata
  let apiPath = (await isDefaultLocale(currentLocale))
    ? `/${requestUrl}`
    : `/${currentLocale}/${requestUrl}`;
  // Normalize double slashes if requestUrl is empty e.g. /en/ or //
  apiPath = ("/" + apiPath).replace(/\/\/+/g, "/");
  if (apiPath === "/" && !(await isDefaultLocale(currentLocale))) {
    apiPath = `/${currentLocale}`; // for homepages like /en not /en/
  }

  logger.info(`[SeoHead generateMetadata] Resolved API path: ${apiPath}`);
  logger.info(
    `[SeoHead generateMetadata] Received searchParams object:`,
    searchParams,
  );

  const seoData = await seoService.getMetadata({
    url: apiPath,
    locale: currentLocale,
    queryParams: searchParams || {},
  });

  if (!seoData) {
    logger.warn(
      `[SeoHead generateMetadata] No SEO data returned from API for path: ${apiPath} and locale: ${currentLocale}`,
      {
        searchParamsUsed: searchParams,
      },
    );
    // Fallback metadata
    const defaultMetadataResult: Metadata = {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      keywords: DEFAULT_KEYWORDS,
      alternates: {
        canonical: `${getBaseUrl()}${apiPath}${searchParams && Object.keys(searchParams).length > 0 ? "?" + buildQueryStringFromFilters(searchParams) : ""}`,
      },
      openGraph: {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        url: `${getBaseUrl()}${apiPath}${searchParams && Object.keys(searchParams).length > 0 ? "?" + buildQueryStringFromFilters(searchParams) : ""}`,
        images: [
          {
            url: `${getBaseUrl()}${DEFAULT_OG_IMAGE}`,
            width: 1200,
            height: 630,
            alt: DEFAULT_TITLE,
          },
        ],
        locale: currentLocale,
        type: "website",
        siteName: "Avezor",
      },
      twitter: {
        card: "summary_large_image",
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        images: [`${getBaseUrl()}${DEFAULT_OG_IMAGE}`],
        creator: "@avezor",
        site: "@avezor",
      },
      robots: {
        index: true,
        follow: true,
        googleBot: {
          index: true,
          follow: true,
          noimageindex: false,
          "max-video-preview": -1,
          "max-image-preview": "large",
          "max-snippet": -1,
        },
      },
    };

    try {
      // const domain = new URL(getBaseUrl()).hostname; // domain isn't used by getSupportedLocales
      const availableLocales = getSupportedLocales(); // Use getSupportedLocales from @/utils/locale
      const siteDefaultLocale = routing.defaultLocale; // Assuming routing is imported and provides defaultLocale

      if (availableLocales.length > 1) {
        const languages: Record<string, string> = {};
        const requestUrl = params?.slug // Use optional chaining here
          ? Array.isArray(params.slug)
            ? params.slug.join("/")
            : params.slug
          : "";

        for (const altLocale of availableLocales) {
          let finalPathForAltLocale: string;
          // Construct path for alternate locale
          if (altLocale === siteDefaultLocale) {
            finalPathForAltLocale = `/${requestUrl}`;
          } else {
            finalPathForAltLocale = `/${altLocale}/${requestUrl}`;
          }
          // Normalize the path
          finalPathForAltLocale = ("/" + finalPathForAltLocale).replace(
            /\/\/+/g,
            "/",
          );
          if (
            finalPathForAltLocale === "/" &&
            altLocale !== siteDefaultLocale
          ) {
            finalPathForAltLocale = `/${altLocale}`;
          }

          const queryString =
            searchParams && Object.keys(searchParams).length > 0
              ? buildQueryStringFromFilters(searchParams) // Use buildQueryStringFromFilters
              : "";
          languages[altLocale] =
            `${getBaseUrl()}${finalPathForAltLocale}${queryString ? `?${queryString}` : ""}`;
        }

        if (Object.keys(languages).length > 0) {
          if (!defaultMetadataResult.alternates) {
            defaultMetadataResult.alternates = {};
          }
          defaultMetadataResult.alternates.languages = languages;
        }
      }
    } catch (error) {
      logger.error(
        "[SeoHead - generateMetadata] Error generating alternates for default metadata:",
        error,
      );
    }
    return defaultMetadataResult;
  }

  // The currentAbsoluteUrl for applyGeneratedMetadata should include query params for canonicals if needed
  // However, seoData.url or seoData.canonical should ideally come from API with query params if they are part of canonical
  const queryStringForCanonical = searchParams
    ? buildQueryStringFromFilters(searchParams)
    : "";
  const currentAbsoluteUrl = `${getBaseUrl()}${apiPath}${queryStringForCanonical ? `?${queryStringForCanonical}` : ""}`;

  return applyGeneratedMetadata(seoData, currentAbsoluteUrl);
}
